<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { EditorContent, useEditor } from '@tiptap/vue-3'
import StarterKit from '@tiptap/starter-kit'
import Link from '@tiptap/extension-link'
import Placeholder from '@tiptap/extension-placeholder'
import { Markdown } from 'tiptap-markdown'

const props = defineProps<{
  modelValue?: string
  disabled?: boolean
}>()
const emit = defineEmits<{
  'update:modelValue': [value: string]
  blur: []
}>()

const { t } = useI18n()

const focused = ref(false)
const modifierHeld = ref(false)

function syncModifier(event: KeyboardEvent) {
  modifierHeld.value = event.ctrlKey || event.metaKey
}
function clearModifier() {
  modifierHeld.value = false
}

onMounted(() => {
  window.addEventListener('keydown', syncModifier)
  window.addEventListener('keyup', syncModifier)
  window.addEventListener('blur', clearModifier)
})
onBeforeUnmount(() => {
  window.removeEventListener('keydown', syncModifier)
  window.removeEventListener('keyup', syncModifier)
  window.removeEventListener('blur', clearModifier)
})

const editor = useEditor({
  content: props.modelValue ?? '',
  editable: !props.disabled,
  extensions: [
    StarterKit,
    Link.configure({
      openOnClick: false,
      HTMLAttributes: { target: '_blank', rel: 'noopener' },
    }),
    Placeholder.configure({ placeholder: t('component.richTextField.placeholder') }),
    Markdown.configure({ html: false, breaks: true, linkify: true, transformPastedText: true }),
  ],
  editorProps: {
    handleClick(_view, _pos, event) {
      if (!event.ctrlKey && !event.metaKey) return false
      const href = (event.target as HTMLElement | null)?.closest('a')?.getAttribute('href')
      if (!href) return false
      const anchor = document.createElement('a')
      anchor.href = href
      anchor.target = '_blank'
      anchor.rel = 'noopener noreferrer'
      anchor.click()
      return true
    },
  },
  onUpdate: ({ editor }) => emit('update:modelValue', editor.storage.markdown.getMarkdown()),
  onFocus: () => (focused.value = true),
  onBlur: () => {
    focused.value = false
    emit('blur')
  },
})

watch(
  () => props.modelValue,
  (value) => {
    if (!editor.value || editor.value.isFocused) return
    if ((value ?? '') !== editor.value.storage.markdown.getMarkdown()) {
      editor.value.commands.setContent(value ?? '', false)
    }
  },
)

watch(
  () => props.disabled,
  (disabled) => editor.value?.setEditable(!disabled),
)

const marks = [
  {
    icon: 'format_bold',
    name: 'bold',
    action: () => editor.value?.chain().focus().toggleBold().run(),
  },
  {
    icon: 'format_italic',
    name: 'italic',
    action: () => editor.value?.chain().focus().toggleItalic().run(),
  },
  {
    icon: 'format_strikethrough',
    name: 'strike',
    action: () => editor.value?.chain().focus().toggleStrike().run(),
  },
]
const headingLevels = [1, 2, 3] as const
const lists = [
  {
    icon: 'format_list_bulleted',
    name: 'bulletList',
    action: () => editor.value?.chain().focus().toggleBulletList().run(),
  },
  {
    icon: 'format_list_numbered',
    name: 'orderedList',
    action: () => editor.value?.chain().focus().toggleOrderedList().run(),
  },
]

const linkMenu = ref(false)
const linkUrl = ref('')

watch(linkMenu, (open) => {
  if (open) {
    linkUrl.value = editor.value?.getAttributes('link').href ?? ''
  }
})

function applyLink() {
  const url = linkUrl.value.trim()
  const chain = editor.value?.chain().focus().extendMarkRange('link')
  if (!url) {
    chain?.unsetLink().run()
  } else {
    chain?.setLink({ href: url }).run()
  }
  linkMenu.value = false
}
</script>

<template>
  <div class="richTextField">
    <div v-if="editor" class="richTextField__toolbar">
      <v-btn
        v-for="mark in marks"
        :key="mark.name"
        :icon="mark.icon"
        size="small"
        variant="text"
        density="comfortable"
        :active="editor.isActive(mark.name)"
        :disabled="props.disabled"
        @click="mark.action"
      />
      <v-divider vertical class="mx-1" />
      <v-btn
        v-for="level in headingLevels"
        :key="`h${level}`"
        size="small"
        variant="text"
        density="comfortable"
        :active="editor.isActive('heading', { level })"
        :disabled="props.disabled"
        @click="editor.chain().focus().toggleHeading({ level }).run()"
      >
        H{{ level }}
      </v-btn>
      <v-divider vertical class="mx-1" />
      <v-btn
        v-for="list in lists"
        :key="list.name"
        :icon="list.icon"
        size="small"
        variant="text"
        density="comfortable"
        :active="editor.isActive(list.name)"
        :disabled="props.disabled"
        @click="list.action"
      />
      <v-divider vertical class="mx-1" />
      <v-menu v-model="linkMenu" :close-on-content-click="false" location="bottom start">
        <template #activator="{ props: menuProps }">
          <v-btn
            v-bind="menuProps"
            icon="link"
            size="small"
            variant="text"
            density="comfortable"
            :active="editor.isActive('link')"
            :disabled="props.disabled"
          />
        </template>
        <div class="richTextField__linkMenu">
          <v-text-field
            v-model="linkUrl"
            :label="t('component.richTextField.linkUrl')"
            density="compact"
            hide-details
            autofocus
            @keydown.enter.prevent="applyLink"
          />
          <v-btn variant="text" size="small" @click="applyLink">
            {{ t('component.richTextField.apply') }}
          </v-btn>
        </div>
      </v-menu>
    </div>
    <div
      class="richTextField__field"
      :class="{
        'richTextField__field--focused': focused,
        'richTextField__field--disabled': props.disabled,
        'richTextField__field--linkable': modifierHeld,
      }"
    >
      <editor-content class="richTextField__content markdownText" :editor="editor" />
    </div>
  </div>
</template>

<style lang="scss">
.richTextField {
  &__toolbar {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 2px;
    margin-bottom: 2px;
  }

  &__linkMenu {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px;
    background: rgb(var(--v-theme-surface));
    min-width: 280px;
  }

  &__field {
    background-color: rgba(var(--v-theme-on-surface), 0.04);
    border-radius: 4px 4px 0 0;
    box-shadow: inset 0 -1px 0 0 rgba(var(--v-theme-on-surface), 0.42);
    cursor: text;
    transition:
      background-color 0.2s,
      box-shadow 0.2s;

    &:hover {
      background-color: rgba(var(--v-theme-on-surface), 0.08);
      box-shadow: inset 0 -1px 0 0 rgba(var(--v-theme-on-surface), 0.87);
    }

    &--focused {
      box-shadow: inset 0 -2px 0 0 rgb(var(--v-theme-primary));
    }

    &--disabled {
      opacity: 0.6;
      pointer-events: none;
    }

    &--linkable .ProseMirror a {
      cursor: pointer;
    }
  }

  &__content {
    .ProseMirror {
      padding: 12px 16px;
      min-height: 120px;
      outline: none;
      user-select: text;

      > :first-child {
        margin-top: 0;
      }

      p.is-editor-empty:first-child::before {
        content: attr(data-placeholder);
        color: rgba(var(--v-theme-on-surface), 0.6);
        pointer-events: none;
        height: 0;
        float: left;
      }

      ul,
      ol {
        padding-left: 1.5em;
      }
      ul {
        list-style: disc;
      }
      ol {
        list-style: decimal;
      }
      li > p {
        margin-bottom: 0;
      }
    }
  }
}
</style>
